// Sensor Observation for activity feed
export interface SensorObservation {
  observation_id: number;
  sensor_id: string;
  timestamp: string;
  facility_id: string;
  location: { lat: number; lon: number } | null;
  device_count: number;
  face_count: number;
  has_plate: boolean;
}

export interface SensorObservationsResponse {
  observations: SensorObservation[];
  count: number;
}

// Detection Types
export interface WirelessSignal {
  mac_address: string;
  signal_type: 'wifi' | 'ble' | 'bluetooth' | 'tpms';
  rssi: number;
  metadata: Record<string, any> | null;
  detected_at: string;
}

export interface PlateDetection {
  text: string;
  gemini_text: string | null;
  image_url: string | null;
  confidence: number | null;
  gemini_confidence: number | null;
}

export interface GeminiAnalysis {
  contains_face: boolean;
  confidence: number;
  image_quality?: string;
  face_clarity?: number;
  lighting_quality?: string;
  estimated_age_range?: string;
  estimated_gender?: string;
  has_glasses?: boolean;
  glasses_type?: string;
  has_hat?: boolean;
  hat_type?: string;
  hat_logo?: string;
  background_scene?: string;
  face_angle?: string;
  expression?: string;
}

export interface FaceDetection {
  image_id: number;
  image_url: string;
  bbox: Record<string, number>;
  confidence: number;
  gemini_analysis?: GeminiAnalysis | null;
}

export interface Detection {
  observation_id: number;
  persona_id: string;
  timestamp: string;
  sensor_id: string;
  facility_id: string;
  location: { lat: number; lon: number } | null;
  plate: PlateDetection | null;
  faces: FaceDetection[];
  wireless_signals: WirelessSignal[];
  processing_state?: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface DetectionsResponse {
  detections: Detection[];
  count: number;
  offset: number;
  limit: number;
}

export interface DetectionFilters {
  limit?: number;
  offset?: number;
  persona_id?: string;
  facility_id?: string;
  start_date?: string;
  end_date?: string;
  processing_state?: 'pending' | 'processing' | 'completed' | 'failed' | 'all';
}

// Persona Types
export interface Persona {
  persona_id: string;
  device_count: number;
  first_seen: string;
  last_seen: string;
  observation_count: number;
  status: string;
  persona_type: 'person' | 'vehicle' | null;
}

export interface PersonaDetail extends Persona {
  rssi_correlation?: Record<string, number>;
  attributes?: Record<string, any>;
}

export interface PersonasResponse {
  personas: Persona[];
  count: number;
  offset: number;
  limit: number;
}

export interface PersonaFilters {
  facility_id?: string;
  persona_status?: string;
  limit?: number;
  offset?: number;
}

// System Status Types
export interface SystemStatus {
  service: string;
  facility_id: string;
  nhi_service_active: boolean;
  redis_connected: boolean;
  database_connected: boolean;
  websocket_clients: number;
  timestamp: string;
}

export interface HealthCheck {
  status: string;
  service: string;
  timestamp: string;
  facility_id: string;
}

// WebSocket Event Types
export interface WebSocketEvent {
  event: 'persona_detected' | 'alert_created';
  persona_id?: string;
  is_new?: boolean;
  sensor_id?: string;
  timestamp: string;
  alert_type?: string;
  severity?: string;
  new_device_count?: number;
}

// Device & Facility Types
export enum DeviceState {
  PENDING = 'pending',
  CONFIGURED = 'configured',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DISABLED = 'disabled',
}

export interface Device {
  sensor_id: string;
  mac_address: string;
  serial_number?: string;
  hardware_info: Record<string, any>;
  facility_id?: string;
  sensor_name?: string;
  state: DeviceState;
  last_heartbeat?: string;
  created_at: string;
  config?: Record<string, any>;
  location?: { lat: number; lon: number };
}

export interface DeviceFilters {
  state?: DeviceState;
  facility_id?: string;
  search?: string;
  limit?: number;
}

export interface DeviceUpdateRequest {
  sensor_name?: string;
  facility_id?: string;
  config?: Record<string, any>;
  enabled_signals?: string[];
  location?: [number, number];
}

// Store State Types
export interface StoreState {
  // Personas
  personas: Persona[];
  selectedPersona: PersonaDetail | null;
  personasLoading: boolean;
  personasTotalCount: number;
  personasPage: number;
  personasPageSize: number;

  // System Status
  systemStatus: SystemStatus | null;
  healthStatus: HealthCheck | null;

  // WebSocket
  wsConnected: boolean;
  wsStatus: 'connected' | 'disconnected' | 'connecting';
  recentEvents: WebSocketEvent[];

  // Devices
  devices: Device[];
  selectedDevice: Device | null;
  devicesLoading: boolean;

  // Detections
  detections: Detection[];
  selectedDetection: Detection | null;
  detectionsLoading: boolean;
  detectionDrawerOpened: boolean;
  detectionsTotalCount: number;
  detectionsPage: number;
  detectionsPageSize: number;
  detectionsProcessingState: 'pending' | 'processing' | 'completed' | 'failed' | 'all';

  // Persona Detections (for persona detail view)
  personaDetections: Detection[];
  personaDetectionsCount: number;
  personaDetectionsLoading: boolean;

  // Sensor Observations (for activity feed)
  sensorObservations: SensorObservation[];
  sensorObservationsLoading: boolean;

  // Actions
  setPersonas: (personas: Persona[]) => void;
  setSelectedPersona: (persona: PersonaDetail | null) => void;
  setPersonasLoading: (loading: boolean) => void;
  setPersonasTotalCount: (count: number) => void;
  setPersonasPage: (page: number) => void;
  setPersonasPageSize: (size: number) => void;

  setSystemStatus: (status: SystemStatus) => void;
  setHealthStatus: (health: HealthCheck) => void;

  setWsConnected: (connected: boolean) => void;
  setWsStatus: (status: 'connected' | 'disconnected' | 'connecting') => void;
  addRecentEvent: (event: WebSocketEvent) => void;

  setDevices: (devices: Device[]) => void;
  setSelectedDevice: (device: Device | null) => void;
  setDevicesLoading: (loading: boolean) => void;

  setDetections: (detections: Detection[]) => void;
  setSelectedDetection: (detection: Detection | null) => void;
  setDetectionsLoading: (loading: boolean) => void;
  setDetectionDrawerOpened: (opened: boolean) => void;
  setDetectionsTotalCount: (count: number) => void;
  setDetectionsPage: (page: number) => void;
  setDetectionsPageSize: (size: number) => void;
  setDetectionsProcessingState: (state: 'pending' | 'processing' | 'completed' | 'failed' | 'all') => void;
  openDetectionDrawer: (detection: Detection) => void;
  closeDetectionDrawer: () => void;

  // Persona detections actions
  setPersonaDetections: (detections: Detection[]) => void;
  setPersonaDetectionsCount: (count: number) => void;
  setPersonaDetectionsLoading: (loading: boolean) => void;
  clearPersonaDetections: () => void;

  setSensorObservations: (observations: SensorObservation[]) => void;
  setSensorObservationsLoading: (loading: boolean) => void;

  // Fetch actions
  fetchPersonas: (filters?: PersonaFilters, showLoading?: boolean) => Promise<void>;
  fetchPersona: (id: string) => Promise<void>;
  fetchSystemStatus: () => Promise<void>;
  fetchDevices: (filters?: DeviceFilters) => Promise<void>;
  fetchDevice: (sensorId: string) => Promise<void>;
  fetchDetections: (filters?: DetectionFilters) => Promise<void>;
  fetchPersonaDetections: (personaId: string) => Promise<void>;
  fetchSensorObservations: () => Promise<void>;
}
